import React from 'react';
import PropTypes from 'prop-types';
import throttle from 'lodash.throttle';
import { bemGenerator, noop } from '@prisjakt/ui-base';
import { AppContext } from '@prisjakt/ui-app-context';

import { connect } from 'react-redux';
import { withAds } from '../../context';
import AdContextType from '../../context/type';
import { AppNexusContextType } from './provider';
import { getUpdates } from './helpers';
import '../../index.scss';

const styles = bemGenerator('pj-ui-ad-container', ['placeholder', 'cogwheel']);

// A class name that should be blocked by most major ad blockers. Used to save space when an ad blocker is active.
// This particular class name is arbitrarily chosen from https://easylist-downloads.adblockplus.org/easylist.txt
const adBlockerClassName = 'AdBox';
const adLoadedAction = (placement, obj) => ({
  type: 'AD_LOADED',
  placement,
  ad: obj,
});

class AppNexusContainer extends React.PureComponent {
  constructor(props) {
    super(props);

    this.formats = props.formats.concat(props.format).filter(Boolean);

    if (!this.formats || this.formats.length === 0) {
      throw new Error(
        `Missing format for ad container with placement: ${props.placement}`
      );
    }

    this.state = {
      width: undefined,
      height: undefined,
      placementId: null,
      placementKey: null,
      placementOptions: null,
      hasBid: false,
      updating: false,
    };
    this.log = props.adContext ? props.adContext.actions.log : () => {};
    this.log('[AppNexus:Ad] CREATED', props.placement);
    this.lastUpdate = 0;
    this.update = () => {
      const time = Date.now();
      if (time - this.lastUpdate >= 2000) {
        this.lastUpdate = time;
        this.updateCurrentPlacement();
      }
    };
    // Listen for resize events and scale ad if necessary
    this.resizeHandler = throttle(
      () => requestAnimationFrame(() => this.handleScalable()),
      200
    );
  }

  componentDidMount() {
    window.addEventListener('resize', this.resizeHandler);
    window.addEventListener('orientationchange', this.resizeHandler);
    this.update();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resizeHandler);
    window.removeEventListener('orientationchange', this.resizeHandler);
    if (this.state.placementId) {
      this.removeListeners(this.state.placementId);
    }
  }

  isWithinLimits() {
    const { windowWidth } = this.props.adContext.state;
    const { min, max } = this.props;
    return (
      (min === undefined || min < windowWidth) &&
      (max === undefined || max > windowWidth)
    );
  }

  isTakeover() {
    const { placementId, width } = this.state;

    if (!placementId) {
      return false;
    }

    return placementId.indexOf('ad_panorama_top') === 0 && width > 980;
  }

  isLegacyMode() {
    return ['desktop', 'native', 'mobile'].includes(this.context.renderMode);
  }

  updateCurrentPlacement() {
    if (this.state.updating) {
      this.log('[AppNexus:Ad] ABORT UPDATE');
      return;
    }
    this.setState(
      {
        placementId: null,
        placementKey: null,
        placementOptions: null,
        updating: true,
      },
      () => {
        const { actions } = this.props.adServiceContext;
        const placementOptions = actions.findPlacement(this.props.placement);
        const placementKey = placementOptions ? placementOptions.key : null;
        if (placementKey) {
          this.setState({
            placementId: placementOptions.containerId,
            placementKey: placementOptions.key,
            placementOptions,
            updating: false,
          });
        } else {
          this.setState({ updating: false });
        }
      }
    );
  }

  getCurrentMaxHeight() {
    return this.state.placementOptions.sizes.reduce((height, size) => {
      const [w, h] = size;

      // All native ads areas has a placement with an 1x1 size
      // Ignore these in the maxHeight calculation
      if (h === 1 && w === 1) {
        return height;
      }

      if (w > 980) {
        return height;
      }
      return height === null ? h : Math.min(h, height);
    }, null);
  }

  componentDidUpdate(prevProps, prevState) {
    const { adServiceContext } = this.props;
    const { placementId, placementKey, placementOptions } = this.state;
    const contextUpdates = getUpdates(
      prevProps.adContext.state,
      this.props.adContext.state,
      ['pathKey', 'pageType', 'siteMode']
    );
    const stateUpdates = getUpdates(prevState, this.state, [
      'placementId',
      'placementKey',
    ]);
    const viewUpdates = getUpdates(
      prevProps.adContext.state,
      this.props.adContext.state,
      ['view']
    );
    if (Object.values(contextUpdates).some(item => !!item)) {
      this.update();
    } else if (
      Object.values(viewUpdates).some(item => !!item) &&
      this.state.placementId
    ) {
      // Triggers visiblity update
      this.props.onStatusChange(
        { targetId: this.state.placementId },
        'adViewUpdated'
      );
    }
    if (stateUpdates.placementId) {
      this.log(
        '[AppNexus:Ad] PLACEMENT UPDATE',
        `| ${prevState.placementId} > ${placementId}`,
        `| ${prevState.placementKey} > ${placementKey}`
      );
      if (placementId) {
        this.addListeners(placementId);
      }
      if (prevState.placementId) {
        this.removeListeners(prevState.placementId);
      }
      this.props.onStatusChange({ targetId: placementId }, 'adUpdated');
    }
    if (stateUpdates.placementKey && placementOptions) {
      adServiceContext.actions.defineTag(
        { ...placementOptions, formats: this.formats },
        this.props.async
      );
    }
    const becameVisible = !prevProps.isVisible && this.props.isVisible;
    const becameReady = !prevProps.isReady && this.props.isReady;
    if (
      (becameVisible || becameReady) &&
      this.props.isReady &&
      this.props.isVisible &&
      this.state.hasBid
    ) {
      this.log('[AppNexus:Ad] VISIBLE', this.props.placement);
      this.props.onStatusChange({ targetId: placementId }, 'adReady');
      if (this.props.autoLoad) {
        this.props.adServiceContext.actions.queue(apntag => {
          apntag.showTag(placementId);
        });
      }
    }
  }

  addListeners(id) {
    const { actions } = this.props.adServiceContext;
    actions.queue(apntag => {
      apntag.onEvent('adAvailable', id, obj => {
        this.log('[AppNexus:Ad] adAvailable', id);
        this.setState(
          {
            hasBid: true,
            width: obj.width,
            height: obj.height,
          },
          () => {
            this.handleScalable();
            this.props.onStatusChange(obj, 'adAvailable');
          }
        );
        if (obj.targetId.indexOf('ad_panorama_top') === 0 && obj.width > 980) {
          // Special case for panorama_top "takeover" in legacy context
          const maxHeight = this.getCurrentMaxHeight();
          if (maxHeight) {
            this.setState({ maxHeight });
          }
        }
        if (this.props.isVisible && this.props.isReady) {
          this.log('[AppNexus:Ad] VISIBLE ON LOAD', id);
          this.props.onStatusChange(obj, 'adReady');
          if (this.props.autoLoad) {
            apntag.showTag(id);
          }
        }
      });
      apntag.onEvent('adLoaded', id, obj => {
        this.log('[AppNexus:Ad] adLoaded', id, obj);
        this.props.onStatusChange(obj, 'adLoaded');
        this.props.adLoadedAction(this.props.placement, obj);
      });
      apntag.onEvent('adNoBid', id, obj => {
        this.log('[AppNexus:Ad] adNoBid', id);
        this.setState({ hasBid: false }, () =>
          this.props.onStatusChange(obj, 'adNoBid')
        );
      });
    });
  }

  removeListeners(id) {
    const { actions } = this.props.adServiceContext;
    actions.queue(apntag => {
      apntag.offEvent('adAvailable', id);
      apntag.offEvent('adLoaded', id);
      apntag.offEvent('adNoBid', id);
    });
  }

  handleScalable() {
    const { adContainer } = this;
    const { scalable } = this.props;

    if (!adContainer || !scalable || this.isTakeover()) {
      return;
    }

    const { scaleBase, scaleParent, scaleOrigin } = this.props;
    const scaleBaseElement = adContainer.closest(scaleBase);
    const containerWidth = adContainer.offsetWidth;

    const baseWidth = scaleBaseElement
      ? scaleBaseElement.offsetWidth
      : window.innerWidth;

    // Do not resize when base is larger than ad, reset any transformations
    const scaleBy = baseWidth / containerWidth;
    if (scaleBy > 1) {
      adContainer.style.transform = 'none';

      if (scaleParent) {
        const parentEl = adContainer.closest(scaleParent);
        parentEl.style.height = 'auto';
        parentEl.style.width = 'auto';
      }

      return;
    }

    adContainer.style.transform = `scale(${scaleBy})`;
    adContainer.style.transformOrigin = scaleOrigin;

    // Scaled elements as the one above will not affect parent nodes, i.e.
    // containers will not adapt to a scaled child element, adjust dimensions to match.
    if (scaleParent) {
      const parentEl = adContainer.closest(scaleParent);
      const { offsetHeight, offsetWidth } = adContainer;
      const { top = 0, bottom = 0 } = this.props.spacing || {};

      const heightWithSpacing =
        offsetHeight + parseInt(top, 10) + parseInt(bottom, 10);

      parentEl.style.height = `${Math.round(heightWithSpacing * scaleBy)}px`;
      parentEl.style.width = `${Math.round(offsetWidth * scaleBy)}px`;
    }
  }

  render() {
    const { width, height, isReady, spacing, order } = this.props;
    const { placementId, placementOptions, maxHeight, hasBid } = this.state;

    const options = {
      ...(placementOptions || {}),
      ...this.props.adServiceContext.actions.getPlacementOptions(
        this.props.placement
      ),
    };

    if (!placementOptions) {
      return options.reserveSpace && this.isWithinLimits() ? (
        <span
          className={`${styles.placeholder} ${adBlockerClassName}`}
          style={{
            width: options.width,
            height: options.height,
            marginLeft: 'auto',
            marginRight: 'auto',
            marginTop: (spacing && spacing.top) || 0,
            marginBottom: (spacing && spacing.bottom) || 0,
          }}
        />
      ) : null;
    }

    const display =
      this.isWithinLimits() && ((isReady && hasBid) || options.reserveSpace);
    const takeoverStyles =
      this.isTakeover() && !this.isLegacyMode()
        ? {
            maxHeight: 'none',
            marginTop: 0,
            marginLeft: 0,
            marginRight: 0,
            overflow: 'hidden',
            border: 0,
          }
        : {};

    return (
      <div
        id={placementId}
        ref={div => {
          this.adContainer = div;
        }}
        className={`${styles.block.withModifier({
          active: display,
          takeover: this.isTakeover(),
          legacy: this.isLegacyMode(),
        })} ${adBlockerClassName}`}
        style={{
          order,
          width: width || this.state.width || options.width,
          height: height || this.state.height || options.height,
          maxHeight: maxHeight || 'none',
          marginTop: (spacing && spacing.top) || 0,
          marginBottom: (spacing && spacing.bottom) || 0,
          ...takeoverStyles,
        }}
      >
        <div id={`${placementId}-button`} className={styles.cogwheel} />
        {display ? this.props.children : null}
      </div>
    );
  }
}

const { string, bool, number, func, node, shape, arrayOf } = PropTypes;

AppNexusContainer.propTypes = {
  adContext: AdContextType.isRequired,
  adServiceContext: AppNexusContextType.isRequired,
  placement: string.isRequired,
  format: string,
  formats: arrayOf(string),
  min: number,
  max: number,
  width: string,
  height: string,
  adLoadedAction: PropTypes.func,
  scalable: bool,
  scaleBase: string,
  scaleParent: string,
  scaleOrigin: string,
  isReady: bool, // Use to delay display until content is fetched
  isVisible: bool, // From TrackVisibility-wrapper
  autoLoad: bool,
  onStatusChange: func,
  children: node,
  spacing: shape({
    top: string,
    bottom: string,
  }),
  async: bool,
  order: number,
};

AppNexusContainer.defaultProps = {
  min: undefined,
  max: undefined,
  format: undefined,
  formats: [],
  width: undefined,
  height: undefined,
  scalable: false,
  scaleBase: null,
  scaleParent: null,
  scaleOrigin: 'top left',
  isReady: true,
  isVisible: true,
  autoLoad: true,
  onStatusChange: noop,
  adLoadedAction: noop,
  children: null,
  spacing: null,
  async: false,
  order: undefined,
};

AppNexusContainer.contextType = AppContext;

const AppNexusContainerWithAds = withAds(props =>
  props.adContext && props.adServiceContext ? (
    <AppNexusContainer {...props} />
  ) : null
);

AppNexusContainerWithAds.propTypes = {
  adContext: AdContextType,
  adServiceContext: AppNexusContextType,
};

AppNexusContainerWithAds.defaultProps = {
  adContext: undefined,
  adServiceContext: undefined,
};

export default connect(undefined, { adLoadedAction })(AppNexusContainerWithAds);
